/**
 * Ruta del tablero de solicitudes (`/loans/pipeline`). `?branchId=` recuerda
 * la agencia elegida por quien ve todas las agencias.
 */
import { useCallback, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FilePlus2, List } from 'lucide-react'
import { z } from 'zod'
import { idSchema } from '@tumin/domain'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { STALE_STAGE_DAYS } from '@/features/loans/loan-rules'
import { useSeesAllBranches } from '@/features/clients/api'
import { PipelineBoard } from '@/features/loans/pipeline-board'
import { ANY_OPTION, BranchFilter } from '@/features/reports/filters'
import { usePermission } from '@/lib/permissions'
import { requirePermission } from '@/lib/route-guards'
import { ROUTES } from '@/lib/routes'
import { useShortcuts, type ShortcutDef } from '@/lib/shortcuts'

/** `?branchId=`: agencia del tablero; un valor inválido se ignora (todas las agencias visibles). */
const pipelineSearchSchema = z.object({
  branchId: idSchema.optional().catch(undefined),
})

export const Route = createFileRoute('/_app/loans/pipeline')({
  beforeLoad: requirePermission('loans:read'),
  validateSearch: pipelineSearchSchema,
  component: LoansPipelinePage,
})

/** Tablero de solicitudes por etapa, con filtro de agencia para quien ve varias. */
function LoansPipelinePage() {
  const navigate = useNavigate()
  const { branchId } = Route.useSearch()
  const seesAllBranches = useSeesAllBranches()
  const routeNavigate = Route.useNavigate()
  const onBranchChange = useCallback(
    (next: string) => void routeNavigate({ search: { branchId: next === ANY_OPTION ? undefined : next }, replace: true }),
    [routeNavigate],
  )
  const canOriginate = usePermission('loans:originate')
  const shortcuts = useMemo<ShortcutDef[]>(
    () =>
      canOriginate
        ? [{ id: 'loan-new', group: 'Acciones', keys: ['N'], sequence: ['n'], label: 'Nueva solicitud', run: () => void navigate({ to: ROUTES.newLoan }) }]
        : [],
    [canOriginate, navigate],
  )
  useShortcuts(shortcuts)

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Fábrica de créditos"
        title="Solicitudes"
        description={`Solicitudes en trámite por etapa. Las tarjetas resaltadas llevan más de ${String(STALE_STAGE_DAYS)} días en su etapa.`}
        actions={
          <>
            <Button variant="outline" onClick={() => void navigate({ to: ROUTES.loans })}>
              <List /> Ver cartera
            </Button>
            {canOriginate && (
              <Button onClick={() => void navigate({ to: ROUTES.newLoan })}>
                <FilePlus2 /> Nueva solicitud
              </Button>
            )}
          </>
        }
      />
      {seesAllBranches && (
        <div className="max-w-xs">
          <BranchFilter id="pipeline-branch" value={branchId ?? ANY_OPTION} onChange={onBranchChange} />
        </div>
      )}
      <PipelineBoard branchId={branchId} />
    </div>
  )
}
